import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import SEO from './SEO';
import { useLanguageToggle } from '../contexts/LanguageToggleContext';
import { useAuth } from '../contexts/AuthContext';
import ItemCard from './ItemCard';
import { api } from '../services/api';
import { translations } from '../translations';

const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
];

interface GrammarPageProps {
  selectedLanguage?: string;
}

type GrammarItem = {
  en: string;
  hi: string;
  bn: string;
  mr: string;
  te: string;
  ta: string;
  gu: string;
  [key: string]: string;
};

// Parts of Speech with examples
const partsOfSpeech = {
  noun: [
    { en: 'The cat is sleeping.', hi: 'बिल्ली सो रही है।', bn: 'বিড়াল ঘুমাচ্ছে।', mr: 'मांजर झोपत आहे.', te: 'పిల్లి నిద్రిస్తోంది.', ta: 'பூனை தூங்குகிறது.', gu: 'બિલાડી ઊંઘી રહી છે.' },
    { en: 'I love reading books.', hi: 'मुझे किताबें पढ़ना पसंद है।', bn: 'আমি বই পড়তে ভালোবাসি।', mr: 'मला पुस्तके वाचायला आवडतात.', te: 'నాకు పుస్తకాలు చదవడం ఇష్టం.', ta: 'நான் புத்தகங்களைப் படிப்பதை விரும்புகிறேன்.', gu: 'મને પુસ્તકો વાંચવાનું પસંદ કરે છે.' },
    { en: 'The sun is bright today.', hi: 'आज सूरज चमक रहा है।', bn: 'আজ সূর্য উজ্জ্বল।', mr: 'आज सूर्य तेजस्वी आहे.', te: 'నేడు సూర్యుడు ప్రకాశవంతంగా ఉన్నాడు.', ta: 'இன்று சூரியன் பிரகாசமாக உள்ளது.', gu: 'આજે સૂર્ય તેજસ્વી છે.' },
    { en: 'She has a beautiful house.', hi: 'उसके पास एक सुंदर घर है।', bn: 'তার একটি সুন্দর বাড়ি আছে।', mr: 'तिच्याकडे एक सुंदर घर आहे.', te: 'ఆమె వద్ద ఒక అందమైన ఇల్లు ఉంది.', ta: 'அவளிடம் ஒரு அழகான வீடு உள்ளது.', gu: 'તેણી પાસે એક સુંદર ઘર છે.' },
    { en: 'The car is fast.', hi: 'कार तेज है।', bn: 'গাড়ি দ্রুত।', mr: 'कार वेगवान आहे.', te: 'కారు వేగవంతంగా ఉంది.', ta: 'கார் வேகமாக உள்ளது.', gu: 'કાર ઝડપી છે.' },
    { en: 'Alas! The game is over.', hi: 'हाय! खेल खत्म हो गया।', bn: 'হায়! খেলা শেষ হয়ে গেছে।', mr: 'अरेरे! खेळ संपला.', te: 'అయ్యో! ఆట ముగిసింది.', ta: 'ஐயோ! விளையாட்டு முடிந்துவிட்டது.', gu: 'હાય! રમત પૂરી થઈ ગઈ.' },
  ],
  verb: [
    { en: 'She runs every morning.', hi: 'वह हर सुबह दौड़ती है।', bn: 'সে প্রতিদিন সকালে দৌड়ায়।', mr: 'ती दररोज सकाळी धावते.', te: 'ఆమె ప్రతి ఉదయం పరుగెత్తుతుంది.', ta: 'அவள் தினமும் காலையில் ஓடுகிறாள்.', gu: 'તે દરેક સવારે દોડે છે.' },
    { en: 'They are studying for the exam.', hi: 'वे परीक्षा के लिए पढ़ रहे हैं।', bn: 'তারা পরীক্ষার জন্য পড়ছে।', mr: 'ते परीक्षेसाठी अभ्यास करत आहेत.', te: 'వారు పరీక్ష కోసం చదువుతున్నారు.', ta: 'அவர்கள் தேர்வுக்காக படிக்கிறார்கள்.', gu: 'તેઓ પરીક્ષા માટે અભ્યાસ કરે છે.' },
    { en: 'I will help you tomorrow.', hi: 'मैं कल आपकी मदद करूंगा।', bn: 'আমি কাল তোমাকে সাহায্য করব।', mr: 'मी उद्या तुला मदत करेन.', te: 'నేను రేపు మీకు సహాయం చేస్తాను.', ta: 'நான் நாளை உங்களுக்கு உதவுவேன்.', gu: 'હું કાલે તમારી મદદ કરીશ.' },
    { en: 'Birds fly in the sky.', hi: 'पक्षी आकाश में उड़ते हैं।', bn: 'পাখিরা আকাশে উড়ে।', mr: 'पक्षी आकाशात उडतात.', te: 'పక్షులు ఆకాశంలో ఎగురుతాయి.', ta: 'பறவைகள் வானில் பறக்கின்றன.', gu: 'પંખીઓ આકાશમાં ઉડે છે.' },
    { en: 'He eats an apple every day.', hi: 'वह हर दिन एक सेब खाता है।', bn: 'সে প্রতিদিন একটি আপেল খায়।', mr: 'तो दररोज सफरचंद खातो.', te: 'అతను ప్రతి రోజు ఆపిల్ తింటాడు.', ta: 'அவன் தினமும் ஆப்பிள் சாப்பிடுகிறான்.', gu: 'તે દરરોજ સફરજન ખાય છે.' },
  ],
  adjective: [
    { en: 'The beautiful flower blooms.', hi: 'सुंदर फूल खिलता है।', bn: 'সুন্দর ফুল ফুটেছে।', mr: 'सुंदर फूल फुलते.', te: 'అందమైన పువ్వు వికసిస్తుంది.', ta: 'அழகான மலர் பூக்கிறது.', gu: 'સુંદર ફૂલ ખીલે છે.' },
    { en: 'The tall man walks slowly.', hi: 'लंबा आदमी धीरे चलता है।', bn: 'লম্বা লোকটি ধীরে হাঁটছে।', mr: 'उंच माणूस हळू चालतो.', te: 'పొడవైన వ్యక్తి నెమ్మదిగా నడుస్తాడు.', ta: 'உயரமான மனிதன் மெதுவாக நடக்கிறான்.', gu: 'ઊંચો માણસ ધીમે ચાલે છે.' },
    { en: 'The red car is fast.', hi: 'लाल कार तेज है।', bn: 'লাল গাড়ি দ্রুত।', mr: 'लाल कार वेगवान आहे.', te: 'ఎరుపు కారు వేగంగా ఉంది.', ta: 'சிவப்பு கார் வேகமாக உள்ளது.', gu: 'લાલ કાર ઝડપી છે.' },
  ],
  adverb: [
    { en: 'She speaks clearly.', hi: 'वह स्पष्ट बोलती है।', bn: 'সে স্পষ্টভাবে কথা বলে।', mr: 'ती स्पष्ट बोलते.', te: 'ఆమె స్పష్టంగా మాట్లాడుతుంది.', ta: 'அவள் தெளிவாக பேசுகிறாள்.', gu: 'તે સ્పષ્ట બોલે છે.' },
    { en: 'He runs quickly.', hi: 'वह तेजी से दौड़ता है।', bn: 'সে দ্রুত দৌड़ায়।', mr: 'तो वेगाने धावतो.', te: 'అతను వేగంగా పరుగెత్తుతాడు.', ta: 'அவன் வேகமாக ஓடுகிறான்.', gu: 'તે ઝડપથી દોડે છે.' },
    { en: 'They work hard.', hi: 'वे मेहनत करते हैं।', bn: 'তারা কঠোর পরিশ্রম করে।', mr: 'ते कष्ट करतात.', te: 'వారు కష్టపడి పని చేస్తారు.', ta: 'அவர்கள் கடினமாக உழைக்கிறார்கள்.', gu: 'તેઓ મહેનત કરે છે.' },
  ],
  pronoun: [
    { en: 'I am going to school.', hi: 'मैं स्कूल जा रहा हूँ।', bn: 'আমি স্কুলে যাচ্ছি।', mr: 'मी शाळेत जात आहे.', te: 'నేను పాఠశాలకు వెళ్తున్నాను.', ta: 'நான் பள்ளிக்கு போகிறேன்.', gu: 'હું శాళామాంం జఈ రహేంది.' },
    { en: 'You are my friend.', hi: 'तुम मेरे दोस्त हो।', bn: 'তুমি আমার বন্ধু।', mr: 'तू माझा मित्र आहेस.', te: 'మీరు నా స్నేహితుడు.', ta: 'நீங்கள் என் நண்பர்.', gu: 'તમે મારા મિત્ર છો.' },
    { en: 'She loves reading books.', hi: 'वह किताबें पढ़ना पसंद करती है।', bn: 'সে বই পড়তে ভালোবাসে।', mr: 'ती पुस्तके वाचायला आवडते.', te: 'ఆమె పుస్తకాలు చదవడం ఇష్టపడుతుంది.', ta: 'அவள் புத்தகங்கள் படிப்பதை விரும்புகிறாள்.', gu: 'તે પુસ્તકો વાંચવાનું પસંદ કરે છે.' },
  ],
  preposition: [
    { en: 'The book is on the table.', hi: 'किताब मेज पर है।', bn: 'বই টেবিলে আছে।', mr: 'पुस्तक टेबलावर आहे.', te: 'పుస్తకం టేబుల్ మీద ఉంది.', ta: 'புத்தகம் மேசையில் உள்ளது.', gu: 'પુસ્તક ટેબલ પર છે.' },
    { en: 'I will meet you at the park.', hi: 'मैं आपसे पार्क में मिलूंगा।', bn: 'আমি তোমার সাথে পার্কে দেখা করব।', mr: 'मी तुला पार्कात भेटेन.', te: 'నేను మిమ్మల్ని పార్క్‌లో కలుస్తాను.', ta: 'நான் உங்களை பூங்காவில் சந்திப்பேன்.', gu: 'હું તમને પાર્કમાં મળીશ.' },
    { en: 'She walked through the door.', hi: 'वह दरवाजे से गुजरी।', bn: 'সে দরজা দিয়ে হেঁটে গেল।', mr: 'ती दारातून गेली.', te: 'ఆమె తలుపు గుండా నడిచింది.', ta: 'அவள் கதவு வழியாக நடந்தாள்.', gu: 'તે દરવાજામાંથી ગઈ.' },
  ],
  conjunction: [
    { en: 'I like tea and coffee.', hi: 'मुझे चाय और कॉफी पसंद है।', bn: 'আমি চা এবং কফি পছন্দ করি।', mr: 'मला चहा आणि कॉफी आवडते.', te: 'నాకు టీ మరియు కాఫీ ఇష్టం.', ta: 'எனக்கு தேநீர் மற்றும் காபி பிடிக்கும்.', gu: 'મને ચા અને કોફી ગમે છે.' },
    { en: 'She is tired but happy.', hi: 'वह थकी हुई है लेकिन खुश है।', bn: 'সে ক্লান্ত কিন্তু সুখী।', mr: 'ती थकलेली आहे पण आनंदी आहे.', te: 'ఆమె అలసిపోయింది కానీ సంతోషంగా ఉంది.', ta: 'அவள் சோர்வாக உள்ளாள் ஆனால் மகிழ்ச்சியாக உள்ளாள்.', gu: 'તે થાકી ગઈ છે પણ ખુશ છે.' },
    { en: 'I will go if you come.', hi: 'अगर तुम आओगे तो मैं जाऊंगा।', bn: 'তুমি এলে আমি যাব।', mr: 'तू आलास तर मी जाईन.', te: 'మీరు వస్తే నేను వెళ్తాను.', ta: 'நீங்கள் வந்தால் நான் போவேன்.', gu: 'તમે આવશો તો હું જઈશ.' },
  ],
  interjection: [
    { en: 'Wow! That is amazing.', hi: 'वाह! यह अद्भुत है।', bn: 'বাহ! এটা আশ্চর্যজনক।', mr: 'वाह! ते आश्चर्यकारक आहे.', te: 'వావ్! అది అద్భుతమైనది.', ta: 'வாவ்! அது ஆச்சரியமாக இருக்கிறது.', gu: 'વાહ! તે આશ્ચર્યજનક છે.' },
    { en: 'Oh! I forgot my keys.', hi: 'ओह! मैं अपनी चाबी भूल गया।', bn: 'ওহ! আমি আমার চাবি ভুলে গেছি।', mr: 'अरे! मी माझ्या चाव्या विसरलो.', te: 'ఓహ్! నేను నా తాళాలు మరచిపోయాను.', ta: 'ஓ! நான் என் சாவிகளை மறந்துவிட்டேன்.', gu: 'ઓહ! હું મારી ચાવી ભૂલી ગયો.' },
    { en: 'Alas! The game is over.', hi: 'हाय! खेल खत्म हो गया।', bn: 'হায়! খেলা শেষ হয়ে গেছে।', mr: 'अरेरे! खेळ संपला.', te: 'అయ్యో! ఆట ముగిసింది.', ta: 'ஐயோ! விளையாட்டு முடிந்துவிட்டது.', gu: 'હાય! રમત પૂરી થઈ ગઈ.' },
  ],
  tenses: {
    present: [
      { key: 'present', label: 'Present' },
    ],
    past: [
      { key: 'past', label: 'Past' },
    ],
    future: [
      { key: 'future', label: 'Future' },
    ],
  },
  advanced: {
    conditionals: [
      { key: 'conditionals', label: 'Conditionals' },
    ],
    passiveVoice: [
      { key: 'passiveVoice', label: 'Passive Voice' },
    ],
    reportedSpeech: [
      { key: 'reportedSpeech', label: 'Reported Speech' },
    ],
  },
};

// Add descriptions for each topic and subtopic
// Add topic arrays for category selection just above the export default function
const partsOfSpeechTopics = [
  { key: 'noun', label: 'Noun' },
  { key: 'verb', label: 'Verb' },
  { key: 'adjective', label: 'Adjective' },
  { key: 'adverb', label: 'Adverb' },
  { key: 'pronoun', label: 'Pronoun' },
  { key: 'preposition', label: 'Preposition' },
  { key: 'conjunction', label: 'Conjunction' },
  { key: 'interjection', label: 'Interjection' },
];
const tenseTopics = [
  { key: 'presentSimple', label: 'Present Simple' },
  { key: 'presentContinuous', label: 'Present Continuous' },
  { key: 'presentPerfect', label: 'Present Perfect' },
  { key: 'pastSimple', label: 'Past Simple' },
  { key: 'pastContinuous', label: 'Past Continuous' },
  { key: 'pastPerfect', label: 'Past Perfect' },
  { key: 'futureSimple', label: 'Future Simple' },
  { key: 'futureContinuous', label: 'Future Continuous' },
  { key: 'futurePerfect', label: 'Future Perfect' },
];
const advancedTopics: { key: string; label: string }[] = [
  { key: 'conditionals', label: 'Conditionals' },
  { key: 'passiveVoice', label: 'Passive Voice' },
  { key: 'reportedSpeech', label: 'Reported Speech' },
];

// Safe highlight function that supports multi-word highlights without breaking sentence structure
function highlightByGrammar(sentence: string, highlights: string[] = []) {
  if (!highlights || highlights.length === 0) return sentence;

  // Sort highlights by length (longest first) to handle multi-word phrases
  const sortedHighlights = highlights.sort((a, b) => b.length - a.length);

  // Create a map of positions to highlight
  const highlightMap = new Map();

  // Process each highlight and mark positions
  sortedHighlights.forEach(highlight => {
    const cleanHighlight = highlight.toLowerCase().trim();
    const regex = new RegExp(`\\b${cleanHighlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');

    let match;
    while ((match = regex.exec(sentence)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      highlightMap.set(start, { end, text: match[0] });
    }
  });

  // Convert positions to sorted array
  const positions = Array.from(highlightMap.entries()).sort((a, b) => a[0] - b[0]);

  // Build the result with highlights
  const result = [];
  let lastIndex = 0;

  positions.forEach(([start, { end, text }]) => {
    // Add text before highlight
    if (start > lastIndex) {
      result.push(sentence.slice(lastIndex, start));
    }

    // Add highlighted text
    result.push(
      <span key={`highlight-${start}`} className="bg-yellow-200 font-bold px-1 rounded inline">
        {text}
      </span>
    );

    lastIndex = end;
  });

  // Add remaining text
  if (lastIndex < sentence.length) {
    result.push(sentence.slice(lastIndex));
  }

  // Wrap everything in a single span to ensure inline flow
  return (
    <span className="inline">
      {result.length > 0 ? result : sentence}
    </span>
  );
}

export default function GrammarPage({ selectedLanguage }: GrammarPageProps) {
  const { userData, addCompletedSentence, addMemorizedSentence, addFavoriteSentence, addFavoriteGrammar, addCompletedGrammar, getCompletedGrammar, refreshUserData } = useUserData();
  const { showEnglish, setShowEnglish } = useLanguageToggle();
  const actualSelectedLanguage = selectedLanguage || userData?.selectedLanguage || 'en';
  const topicDescriptions = showEnglish
    ? (translations as any).en.topicDescriptions
    : (translations as any)[actualSelectedLanguage]?.topicDescriptions || (translations as any).en.topicDescriptions;
  const [selectedCategory, setSelectedCategory] = useState<'partsOfSpeech' | 'tenses' | 'advanced'>('partsOfSpeech');
  const [selectedTopic, setSelectedTopic] = useState<string>('noun');
  const [items, setItems] = useState<any[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [grammarTotal, setGrammarTotal] = useState(0);
  const [grammarCompleted, setGrammarCompleted] = useState(0);
  const [page, setPage] = useState(1); // Track current API page
  const [limit] = useState(50); // Always fetch 50 at a time
  const [totalPages, setTotalPages] = useState(1);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [loadingButtons, setLoadingButtons] = useState<{ [key: string]: boolean }>({});
  const [completedCount, setCompletedCount] = useState(0); // Track completed count for live updates

  // Function to handle topic changes and prevent multiple API calls
  const handleTopicChange = useCallback((newTopic: string, newCategory?: 'partsOfSpeech' | 'tenses' | 'advanced') => {
    if (isApiLoading) return; // Prevent changes while API is loading

    console.log('GrammarPage: Changing topic to:', newTopic, 'category:', newCategory);

    // Batch state updates to prevent multiple useEffect triggers
    if (newCategory) {
      setSelectedCategory(newCategory);
    }
    setSelectedTopic(newTopic);
    setPage(1);
    setCurrentBatchIndex(0);
  }, [isApiLoading]);

  useEffect(() => {
    if (!userId || isApiLoading) return; // Prevent duplicate calls while loading

    console.log('GrammarPage: Fetching data for topic:', selectedTopic, 'page:', page, 'userId:', userId);
    setItems([]); // Clear old data immediately
    setCurrentBatchIndex(0); // Reset batch index
    setTotalCount(0); // Reset count
    setTotalPages(1); // Reset pages
    setLoading(true);
    setIsApiLoading(true); // Set API loading state

    api.getMasterGrammar(selectedTopic, userId, page, limit)
      .then(res => {
        console.log('GrammarPage: API response received for topic:', selectedTopic);
        const items = res.items || [];
        setItems(items);
        setCurrentBatchIndex(0);
        setTotalCount(res.totalCount || 0);
        setTotalPages(res.totalPages || 1);

        // Auto-advance to next page if all items are completed/memorized
        if (items.length === 0 && page < (res.totalPages || 1)) {
          console.log('GrammarPage: All items completed on page', page, '- advancing to next page');
          setPage(page + 1);
        }
      })
      .catch(err => {
        console.error('Error fetching grammar data:', err);
        setItems([]);
        setTotalCount(0);
        setTotalPages(1);
      })
      .finally(() => {
        setLoading(false);
        setIsApiLoading(false); // Clear API loading state
      });
  }, [selectedTopic, userId, page, limit]);

  useEffect(() => {
    if (!userId) return;
    setCompletedIds([]); // Clear completed IDs immediately
    setFavoriteIds([]); // Clear favorite IDs immediately
    getCompletedGrammar(selectedTopic).then((res: any[]) => {
      const completed = res.map((g: any) => g.itemId || '');
      setCompletedIds(completed);
      setCompletedCount(completed.length); // Set initial completed count
    });
    // Load favorite grammar IDs for this topic
    if (userId) {
      api.getFavoriteGrammar(userId, selectedTopic).then(favs => {
        setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
      });
    }
  }, [selectedTopic, userId]);

  const batchSize = 3;
  const batchStart = currentBatchIndex * batchSize;
  const batchEnd = batchStart + batchSize;
  const currentBatch = items.slice(batchStart, batchEnd);

  const getTranslation = (item: any, language: string): string => {
    return item[language] || item.en;
  };

  const handleAction = async (action: 'done' | 'memorize' | 'favorite', item: any) => {
    if (action === 'done') {
      if (item._id) {
        await addCompletedGrammar(
          `${selectedTopic}:${item._id}`,
          item.topic || selectedTopic
        );
        if (userId) {
          await api.memorizeGrammar(
            userId,
            `${selectedTopic}:${item._id}`,
            item.topic || selectedTopic,
            'done'
          );
        }
      }
      // Remove from local state
      setItems(prev => prev.filter(i => i._id !== item._id));
    } else if (action === 'memorize') {
      if (currentUser?.uid && item._id) {
        await api.memorizeGrammar(currentUser.uid, `${selectedTopic}:${item._id}`, item.topic || selectedTopic, 'memorized');
        setItems(items => items.filter(i => i._id !== item._id));
      }
    } else if (action === 'favorite') {
      if (item._id) {
        // Optimistically update local state
        setFavoriteIds(prev => [...prev, `${selectedTopic}:${item._id}`]);

        await addFavoriteGrammar(
          `${selectedTopic}:${item._id}`,
          item.topic || selectedTopic
        );

        // Refresh favoriteIds after favoriting (in case of backend changes)
        if (userId) {
          api.getFavoriteGrammar(userId, selectedTopic)
            .then(favs => {
              setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
            })
            .catch(err => {
              console.error('Error refreshing favorite grammar:', err);
            });
        }
      }
      // Do NOT remove from local state when favorited
    }
    // Move to next batch if needed
    const newBatchStart = currentBatchIndex * batchSize;
    if (newBatchStart >= items.length && currentBatchIndex > 0) {
      setCurrentBatchIndex(currentBatchIndex - 1);
    } else {
      setCurrentBatchIndex(i => i);
    }
  };

  // Memoize filtered items to prevent unnecessary re-renders
  const filteredItems = useMemo(() => {
    return items.filter(item => !completedIds.includes(item._id));
  }, [items, completedIds]);

  // Memoize completed count - use completedCount for real-time updates
  const memoizedCompletedCount = useMemo(() => {
    return completedCount;
  }, [completedCount]);

  // Add a function to check if a grammar item is favorited
  const isGrammarFavorite = (item: any) => {
    const favs: { itemId: string }[] = (userData as any)?.favoriteGrammar || [];
    return favs.some((fav: { itemId: string }) => fav.itemId === `${selectedTopic}:${item._id}`);
  };

  const isFavorite = (item: any) => favoriteIds.includes(`${selectedTopic}:${item._id}`);

  const percent = grammarTotal > 0 ? Math.round((grammarCompleted / grammarTotal) * 100) : 0;

  // Next/Prev button handlers for API pagination
  const handleNext = () => {
    if (batchEnd < items.length) {
      setCurrentBatchIndex(i => i + 1);
    } else if (page < totalPages) {
      setPage(p => p + 1);
      setCurrentBatchIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentBatchIndex > 0) {
      setCurrentBatchIndex(i => i - 1);
    } else if (page > 1) {
      setPage(p => p - 1);
      setCurrentBatchIndex(Math.floor((limit / batchSize)) - 1); // last batch of previous page
    }
  };

  const handleDone = async (item: any) => {
    if (!item._id || loadingButtons[`done-${item._id}`]) return;

    // Set loading state for this specific button
    setLoadingButtons(prev => ({ ...prev, [`done-${item._id}`]: true }));

    // Optimistically update UI immediately
    setItems(prev => prev.filter(i => i._id !== item._id));
    setCompletedCount(prev => prev + 1); // Increment completed count immediately

    try {
      // Run API calls in parallel for better performance
      await Promise.all([
        addCompletedGrammar(`${selectedTopic}:${item._id}`, selectedTopic),
        api.memorizeGrammar(userId!, `${selectedTopic}:${item._id}`, selectedTopic, 'done'),
        refreshUserData()
      ]);
    } catch (error) {
      console.error('Error in handleDone:', error);
      // Revert optimistic update on error
      setItems(prev => [...prev, item]);
      setCompletedCount(prev => prev - 1); // Revert completed count on error
    } finally {
      setLoadingButtons(prev => ({ ...prev, [`done-${item._id}`]: false }));
    }
  };

  const handleMemorize = async (item: any) => {
    if (!item._id || loadingButtons[`memorize-${item._id}`]) return;

    // Set loading state for this specific button
    setLoadingButtons(prev => ({ ...prev, [`memorize-${item._id}`]: true }));

    // Optimistically update UI immediately
    setItems(prev => prev.filter(i => i._id !== item._id));
    setCompletedCount(prev => prev + 1); // Increment completed count immediately

    try {
      // Run API calls in parallel for better performance
      await Promise.all([
        api.memorizeGrammar(userId!, `${selectedTopic}:${item._id}`, selectedTopic, 'memorized'),
        refreshUserData()
      ]);
    } catch (error) {
      console.error('Error in handleMemorize:', error);
      // Revert optimistic update on error
      setItems(prev => [...prev, item]);
      setCompletedCount(prev => prev - 1); // Revert completed count on error
    } finally {
      setLoadingButtons(prev => ({ ...prev, [`memorize-${item._id}`]: false }));
    }
  };

  return (
    <>
      <SEO page="grammar" />
      <div className="relative min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4 md:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight drop-shadow-lg mb-4 md:mb-6 text-center">
            <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">English Grammar</span>
          </h1>

          {/* Category Selection - Responsive */}
          <div className="flex flex-wrap gap-1 sm:gap-2 justify-center mb-4 sm:mb-6 md:mb-8">
            <button
              className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${selectedCategory === 'partsOfSpeech' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}
              onClick={() => handleTopicChange('noun', 'partsOfSpeech')}
            >
              Parts of Speech
            </button>
            <button
              className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${selectedCategory === 'tenses' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
              onClick={() => handleTopicChange('presentSimple', 'tenses')}
            >
              Tenses
            </button>
            <button
              className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${selectedCategory === 'advanced' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'}`}
              onClick={() => handleTopicChange('conditionals', 'advanced')}
            >
              Advanced
            </button>
          </div>

          {/* Topic Selection - Responsive */}
          <div className="flex flex-wrap gap-1 sm:gap-2 justify-center mb-4 sm:mb-6 md:mb-8">
            {selectedCategory === 'partsOfSpeech' && (
              <>
                {partsOfSpeechTopics.map((topic) => (
                  <button
                    key={topic.key}
                    className={`px-2 sm:px-3 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${selectedTopic === topic.key ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}
                    onClick={() => handleTopicChange(topic.key)}
                  >
                    {topic.label}
                  </button>
                ))}
              </>
            )}
            {selectedCategory === 'tenses' && (
              <>
                {tenseTopics.map((topic) => (
                  <button
                    key={topic.key}
                    className={`px-2 sm:px-3 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${selectedTopic === topic.key ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
                    onClick={() => handleTopicChange(topic.key)}
                  >
                    {topic.label}
                  </button>
                ))}
              </>
            )}
            {selectedCategory === 'advanced' && (
              <>
                {advancedTopics.map((topic) => (
                  <button
                    key={topic.key}
                    className={`px-2 sm:px-3 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${selectedTopic === topic.key ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'}`}
                    onClick={() => handleTopicChange(topic.key)}
                  >
                    {topic.label}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Navigation Buttons - Below Topics */}
          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="flex flex-row gap-2 items-center">
              <button
                onClick={() => window.location.href = '/memorize-grammar'}
                className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-purple-400 to-purple-500 text-white text-xs font-semibold rounded-full shadow-lg hover:from-purple-500 hover:to-purple-600 hover:shadow-purple-400/50 focus:ring-2 focus:ring-purple-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
              >
                <span className="hidden sm:inline">Memorize</span>
                <span className="sm:hidden">Memorize</span>
              </button>
              <button
                onClick={() => window.location.href = '/favorite-grammar'}
                className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-yellow-300 to-yellow-400 text-white text-xs font-semibold rounded-full shadow-lg hover:from-yellow-400 hover:to-yellow-500 hover:shadow-yellow-400/50 focus:ring-2 focus:ring-yellow-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
              >
                <span className="hidden sm:inline">Favorites</span>
                <span className="sm:hidden">Favorites</span>
              </button>
              <button
                onClick={() => window.location.href = '/completed-grammar'}
                className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-green-400 to-green-500 text-white text-xs font-semibold rounded-full shadow-lg hover:from-green-500 hover:to-green-600 hover:shadow-green-400/50 focus:ring-2 focus:ring-green-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
              >
                <span className="hidden sm:inline">Completed</span>
                <span className="sm:hidden">Completed</span>
              </button>
            </div>
          </div>

          {/* Topic Description */}
          {selectedTopic && topicDescriptions[selectedTopic] && (
            <div className="text-center mb-6 sm:mb-8">
              <div className="max-w-4xl mx-auto bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg border border-blue-200/50 relative">
                {/* Language Toggle - Top Right Corner - Hide when English is selected */}
                {actualSelectedLanguage !== 'en' && (
                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                    <button
                      className={`relative flex items-center px-1 py-0.5 rounded-full border-2 transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 overflow-hidden ${showEnglish ? 'bg-gradient-to-r from-blue-500 via-purple-400 to-pink-400 border-blue-400' : 'bg-white border-blue-200'
                        }`}
                      style={{
                        minWidth: 'auto',
                        height: 24,
                        fontSize: 9,
                        paddingLeft: 4,
                        paddingRight: 4
                      }}
                      onClick={() => setShowEnglish(!showEnglish)}
                      aria-label={showEnglish ? `Show in ${languages.find(l => l.code === actualSelectedLanguage)?.name || 'Selected Language'}` : 'Show in English'}
                    >
                      <span
                        className={`z-10 px-1 py-0.5 text-xs font-semibold rounded-full transition-colors duration-200 ${showEnglish ? 'text-white' : 'text-blue-700'
                          }`}
                        style={{ position: 'relative', zIndex: 2 }}
                      >
                        EN
                      </span>
                      <span
                        className={`z-10 px-1 py-0.5 text-xs font-semibold rounded-full transition-colors duration-200 ${!showEnglish ? 'text-white' : 'text-blue-700'
                          }`}
                        style={{ position: 'relative', zIndex: 2 }}
                      >
                        {actualSelectedLanguage.toUpperCase()}
                      </span>
                      {/* Animated pill background */}
                      <span
                        className={`absolute top-0 left-0 h-full w-1/2 rounded-full transition-all duration-200 pointer-events-none bg-gradient-to-r from-blue-500 via-purple-400 to-pink-400`}
                        style={{
                          width: '50%',
                          transform: showEnglish ? 'translateX(0%)' : 'translateX(100%)',
                          zIndex: 1,
                        }}
                      />
                    </button>
                  </div>
                )}

                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">
                  {selectedTopic.charAt(0).toUpperCase() + selectedTopic.slice(1).replace(/([A-Z])/g, ' $1')}
                </h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {topicDescriptions[selectedTopic]}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Congratulations!
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                You have completed all grammar items for this topic.
              </p>
              <p className="text-gray-500 text-xs sm:text-sm mt-2">
                Great job! Keep practicing to maintain your skills.
              </p>
            </div>
          ) : (
            <div className="mb-8">
              {/* Count Display */}
              <div className="text-center mb-4">
                <p className="text-sm sm:text-base text-gray-600">
                  Showing {memoizedCompletedCount} of {totalCount} items in {selectedTopic} category
                </p>
              </div>
              <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 justify-center items-stretch">
                {currentBatch.map((item, idx) => {
                  const favoriteButton = (
                    <button
                      className={`px-3 sm:px-4 md:px-6 py-2 rounded-full text-xs sm:text-sm bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-semibold shadow-lg hover:from-yellow-500 hover:to-yellow-700 hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${isFavorite(item) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : ''}`}
                      onClick={async () => {
                        if (isFavorite(item)) return;
                        if (currentUser?.uid && item._id) {
                          // Optimistically update favoriteIds
                          setFavoriteIds(prev => [...prev, `${selectedTopic}:${item._id}`]);
                          await api.addFavoriteGrammar(currentUser.uid, `${selectedTopic}:${item._id}`, selectedTopic);
                          await refreshUserData();
                          // Refresh favoriteIds after favoriting
                          api.getFavoriteGrammar(currentUser.uid, selectedTopic).then(favs => {
                            setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
                          });
                        }
                      }}
                      disabled={isFavorite(item)}
                      title={isFavorite(item) ? 'Already favorited' : 'Favorite'}
                    >
                      <span>⭐</span> <span className="hidden sm:inline">Favorite</span>
                    </button>
                  );
                  return (
                    <div key={item.en + idx} className="mb-4 w-full lg:w-1/3 bg-white rounded-xl shadow p-3 sm:p-4 flex flex-col gap-2">
                      <ItemCard
                        item={{
                          word: item.en,
                          en: item.en,
                          hi: item.hi,
                          bn: item.bn,
                          mr: item.mr,
                          te: item.te,
                          ta: item.ta,
                          gu: item.gu,
                          meaning: item.meaning || item.example,
                          highlight: item.highlight || []
                        }}
                        type="grammar"
                        selectedLanguage={actualSelectedLanguage}
                        isFavorite={isFavorite(item)}
                        hideActions={true}
                        onDone={() => { }}
                        onMemorize={() => { }}
                        onFavorite={() => { }}
                      />
                      <div className="flex flex-wrap gap-1 sm:gap-2 mt-2 justify-center sm:justify-start">
                        <button
                          className="flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-green-400 to-green-600 text-white text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleDone(item)}
                          disabled={loadingButtons[`done-${item._id}`]}
                        >
                          {loadingButtons[`done-${item._id}`] ? (
                            <>
                              <span className="animate-spin text-xs">⏳</span>
                              <span className="hidden sm:inline text-xs">Processing...</span>
                            </>
                          ) : (
                            <>
                              <span className="text-xs">✅</span>
                              <span className="text-xs">Done</span>
                            </>
                          )}
                        </button>
                        <button
                          className="flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 text-white text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleMemorize(item)}
                          disabled={loadingButtons[`memorize-${item._id}`]}
                        >
                          {loadingButtons[`memorize-${item._id}`] ? (
                            <>
                              <span className="animate-spin text-xs">⏳</span>
                              <span className="hidden sm:inline text-xs">Processing...</span>
                            </>
                          ) : (
                            <>
                              <span className="text-xs">🧠</span>
                              <span className="text-xs">Memorize</span>
                            </>
                          )}
                        </button>
                        <button
                          className={`flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${isFavorite(item) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white hover:from-yellow-500 hover:to-yellow-700'}`}
                          onClick={async () => {
                            if (isFavorite(item)) return;
                            if (currentUser?.uid && item._id) {
                              // Optimistically update favoriteIds
                              setFavoriteIds(prev => [...prev, `${selectedTopic}:${item._id}`]);
                              await api.addFavoriteGrammar(currentUser.uid, `${selectedTopic}:${item._id}`, selectedTopic);
                              await refreshUserData();
                              // Refresh favoriteIds after favoriting
                              api.getFavoriteGrammar(currentUser.uid, selectedTopic).then(favs => {
                                setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
                              });
                            }
                          }}
                          disabled={isFavorite(item)}
                          title={isFavorite(item) ? 'Already favorited' : 'Favorite'}
                        >
                          <span className="text-xs">⭐</span>
                          <span className="text-xs">Favorite</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Navigation Controls - Responsive */}
          <div className="flex justify-center items-center gap-2 sm:gap-4 mt-6 sm:mt-8">
            <button
              onClick={handlePrev}
              disabled={currentBatchIndex === 0 && page === 1}
              className="px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs sm:text-sm font-bold shadow-lg hover:from-blue-500 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              ← <span className="hidden sm:inline">Previous</span>
            </button>
            <button
              onClick={handleNext}
              disabled={currentBatchIndex === currentBatch.length - 1 && page === totalPages}
              className="px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs sm:text-sm font-bold shadow-lg hover:from-blue-500 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <span className="hidden sm:inline">Next</span> →
            </button>
          </div>
        </div>
      </div>
    </>
  );
} 